import type { BulletChartProps } from '@usealloy/component-library';
import capitalize from 'lodash/capitalize';
import type { RiskFactor, RiskScore } from '../types';
import { getRangePadding } from '~/components/ValueRangeConfig/helpers';
import { useMemo } from 'react';

/* TYPES */
interface UseRiskDataArgs {
  isRiskFlagEnabled: boolean;
  isWorkflowTypeReconciliation?: boolean;
  riskScoreData?: {
    [riskCategory: string]: RiskScore;
  };
}

export interface CoreRiskData {
  riskFactors: RiskFactor[];
  riskFormula: Array<string | number>;
  riskRangeValues: BulletChartProps['colorRanges'];
  riskRatingColor: string | null;
  riskRatingLabel: string | null;
  totalRisk: number | null;
}
type UseRiskDataReturn = CoreRiskData & {
  showRiskCard: boolean;
  showRiskTab: boolean;
};

const getFormattedColor = (color: string) => {
  // If it's a component library string, return as-is
  if (color.startsWith('var')) {
    return color;
  }
  return color.startsWith('#') ? color : `#${color}`;
};

export const useRiskData = ({
  isRiskFlagEnabled, isWorkflowTypeReconciliation = false, riskScoreData = {},
}: UseRiskDataArgs): UseRiskDataReturn => useMemo(() => {
  const baseRiskData = {
    riskFactors: [],
    riskFormula: [],
    riskRangeValues: [],
    riskRatingColor: null,
    riskRatingLabel: null,
    showRiskCard: false,
    showRiskTab: false,
    totalRisk: null,
  };
  const hasRiskScoreData = Object.keys(riskScoreData).length > 0;
  if (!isRiskFlagEnabled || !hasRiskScoreData) {
    return baseRiskData;
  }
  const riskAssessmentData = Object.values(riskScoreData)[0];
  const overallRiskScore = riskAssessmentData?.final_calculation;
  if (!riskAssessmentData || !overallRiskScore) {
    return baseRiskData;
  }
  if (!isNaN(Number(overallRiskScore.value))) {
    baseRiskData.totalRisk = Number(overallRiskScore.value);
  }

  if (riskAssessmentData.risk_factors) {
    baseRiskData.riskFactors = riskAssessmentData.risk_factors;
  }

  if (overallRiskScore.formula) {
    baseRiskData.riskFormula = overallRiskScore.formula;
  }

  // Find overall score category/color
  const matchingRange = overallRiskScore.output_value_range?.find(({ minValue, maxValue }, idx) => {
    const riskScore = Number(overallRiskScore?.value);
    const isLastRange = idx === overallRiskScore.output_value_range.length - 1;
    return (minValue ?? -Number.MAX_VALUE) <= riskScore
      && (isLastRange ? riskScore <= (maxValue ?? Number.MAX_VALUE) : riskScore < maxValue);
  });
  if (matchingRange) {
    baseRiskData.riskRatingColor = matchingRange.color;
    baseRiskData.riskRatingLabel = capitalize(matchingRange.label);
  }

  // convert range values to format expected by bullet chart
  if (overallRiskScore.output_value_range?.length > 0) {
    const rangePadding = getRangePadding(overallRiskScore.output_value_range);
    baseRiskData.riskRangeValues = (
      overallRiskScore.output_value_range.map(({
        minValue, maxValue, label, color,
      }) => ({
        from: minValue ?? Math.min(maxValue, baseRiskData.totalRisk) - rangePadding,
        to: maxValue ?? Math.max(minValue, baseRiskData.totalRisk) + rangePadding,
        label: `${capitalize(label)} ${
          // Workflow editor uses Number.MIN_VALUE and Number.MAX_VALUE for unbounded ranges
          // We display these as −∞ and ∞
          // null (range boundary not saved) should also show as infinity
          minValue === Number.MIN_VALUE || minValue === null ? '−∞' : minValue
        } - ${maxValue === Number.MAX_VALUE || maxValue === null ? '∞' : maxValue}`,
        color: getFormattedColor(color),
      }))
    );
  }

  if (isWorkflowTypeReconciliation) {
    return {
      ...baseRiskData,
      // Recon workflows show the card on the main tab, not a separate tab
      showRiskCard: hasRiskScoreData,
    };
  }
  return {
    ...baseRiskData,
    showRiskTab: hasRiskScoreData,
  };
}, [
  isRiskFlagEnabled,
  isWorkflowTypeReconciliation,
  riskScoreData,
]);
